import React, { useEffect, useState } from "react";
import { ProductItemInterface, VariantItem } from "../../../assets/interfaces";
import { capitalizeFirstLetter, enumToHumanFriendly, toCurrency } from "../../../assets/js/utils/functions";
import { checkItemAvailability, getActualPrice } from "../../../assets/js/utils/utils";
import RoundCheckbox from "../../ui/form-elements/round-checkbox";
import { FormatCurrencyFun } from "@/contexts//cart-context";
import classNames from "classnames";
import { SelectDropdown } from "@/components/ui/form-elements";
import Dropdown from "@/components/ui/dropdown-new";

interface Props {
  allImages: string[];
  options: VariantItem[];
  selectedImages: string[];
  extraOption: string;
  selected: { id: string; quantity: number; image: string; values: { [key: string]: string }; error: string }[];
  setSelected: (options: { id: string; quantity: number; image: string; values: { [key: string]: string } }[]) => void;
  formatAsCurrency: FormatCurrencyFun;
  product: ProductItemInterface;
  incrementQty: (id: string) => void;
  decrementQty: (id: string) => void;
  goBack: () => void;
  goForward: () => void;
}

interface OptionProps {
  title: string;
  id: string;
  price: string;
  quantity: number;
  increment: (id: string) => void;
  decrement: (id: string) => void;
  extraOption: string;
  error: string;
}

const SelectVariantExtra: React.FC<Props> = ({
  allImages,
  options,
  selectedImages,
  extraOption,
  selected,
  setSelected,
  formatAsCurrency,
  product,
  incrementQty,
  decrementQty,
  goBack,
  goForward,
}) => {
  const selectedWithOptions = selectedImages.map((s) => {
    return {
      image: s,
      options: options
        .filter((o) => o.image === s && checkItemAvailability(product, o, true))
        .map((o) => ({ option: o.values[extraOption], price: getActualPrice(o), id: o.id, quantity: o.quantity })),
    };
  });

  useEffect(() => {
    //only  run this the first time
    for (let index = 0; index < selectedImages.length; index++) {
      const image = selectedImages[index];

      const hasSomeSelectedValues = selected.some((s) => s.image === image);

      if (!hasSomeSelectedValues) {
        const optionsConfig = selectedWithOptions.find((s) => s.image === image).options[0];
        setSelected([
          ...selected,
          { id: optionsConfig.id, quantity: 1, image, values: { [extraOption]: optionsConfig.option } },
        ]);
      }
    }
  }, []);

  useEffect(() => {
    if (selected.length > 0) {
      const prevSelectedImages = selected.map((s) => ({ [s.id]: options.find((o) => o.id === s.id)?.image }));

      //create a new selected array which is an array of the ids that match with previously selected images
      const newSelected = prevSelectedImages.filter((s) => {
        const image = Object.values(s)[0];

        return selectedImages.includes(image);
      });

      // setSelected(newSelected.map((n) => ({ id: Object.keys(n)[0], quantity: 1 })));
    }

    if (selected.length < 1) {
      const selectedIds = selectedWithOptions.flatMap((n) =>
        n.options.map((option) => ({ id: option.id, quantity: 1 }))
      );
      // setSelected(selectedIds);
    }
  }, []);

  const selectOption = (option: string, image: string, values: { [key: string]: string }) => {
    const selectedCopy = [...selected];
    const optionIndex = selectedCopy.findIndex((item) => item.id === option);

    if (optionIndex > -1) {
      selectedCopy.splice(optionIndex, 1);
      setSelected(selectedCopy);
    } else {
      setSelected([...selectedCopy, { id: option, quantity: 1, image, values }]);
    }
  };

  return (
    <div className="mt-5">
      <span className="text-black-secondary text-1xs sm:text-sm mb-3 font-medium block">
        Please pick at least one {extraOption} for each image:
      </span>
      <ul className="space-y-7">
        {selectedWithOptions.map((s, index) => (
          <li className="border border-grey-divider rounded-10 relative p-2.5" key={index}>
            <div className="flex items-center gap-3.5 w-full mb-4">
              <figure
                className={`h-12.5 w-12.5 rounded-10 overflow-hidden flex-shrink-0 border border-grey-fields-100`}
              >
                <img src={s.image} alt="" className="h-full w-full object-cover bg-grey-fields-200" />
              </figure>
              <h5
                className={`text-black-secondary font-display font-bold overflow-hidden mb-0.5 text-base sm:text-lg max-w-full whitespace-nowrap overflow-ellipsis `}
              >
                Image {index + 1}
              </h5>
            </div>
            <div className="flex flex-col gap-2 w-full items-start">
              {selected
                .filter((o) => s.options.some((option) => option.id === o.id))
                .map((o, index) => (
                  <OptionCard
                    key={index}
                    title={s.options.find((t) => t.id === o.id)?.option}
                    id={o.id}
                    quantity={o.quantity}
                    price={formatAsCurrency(s.options.find((t) => t.id === o.id)?.price * o.quantity)}
                    decrement={decrementQty}
                    increment={incrementQty}
                    extraOption={extraOption}
                    error={o.error}
                  />
                ))}
            </div>
            {!s.options.every((option) => selected.some((item) => item.id === option.id)) && (
              <>
                <SelectDropdown
                  label={`Select New ${enumToHumanFriendly(extraOption)}`}
                  onChange={(value) =>
                    selectOption(value.target.value, s.image, {
                      [extraOption]: s.options.find((o) => o.id === value.target.value)?.option,
                    })
                  }
                  options={s.options
                    .map(
                      (o) =>
                        !selected.some((item) => item.id === o.id) && {
                          text: enumToHumanFriendly(o.option),
                          value: o.id,
                        }
                    )
                    .filter((o) => !!o)}
                />
              </>
            )}
          </li>
        ))}

        {selectedImages.length < allImages.length && (
          <button
            onClick={goBack}
            className="mt-5 mb-3 flex items-center text-primary-500 text-sm font-semibold"
            type="button"
          >
            {/* prettier-ignore */}
            <svg viewBox="0 0 14 14" fill="none" className="w-4 mr-1">
            <path d="M7 2.91675V11.0834" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path>
            <path d="M2.91675 7H11.0834" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
            Add New {enumToHumanFriendly(extraOption)}
          </button>
        )}
      </ul>
    </div>
  );
};

const OptionCard: React.FC<OptionProps> = ({
  title,
  price,
  decrement,
  increment,
  quantity,
  id,
  extraOption,
  error,
}) => {
  const [showContent, setShowContent] = useState(true);
  const transition = "transition-all ease-out duration-300";
  const accordionClasses = classNames(transition, {
    "max-h-0 hidden rounded-b-xl py-2 px-3 md:py-2.5 md:px-3.5 flex justify-between items-center overflow-hidden":
      !showContent,
    "rounded-b-xl py-2 px-3 md:py-2.5 md:px-3.5 flex justify-between items-center": showContent,
  });
  return (
    <>
      <div className="flex w-full flex-col bg-grey-fields-100 rounded-15">
        <div
          className={`flex px-3 md:px-3.5 py-2 md:py-2.5 items-center bg-white border-2 border-grey-fields-100 justify-between rounded-15 cursor-pointer`}
        >
          <span className="font-body text-dark text-sm">
            {enumToHumanFriendly(extraOption)}: {enumToHumanFriendly(title)}
          </span>

          <button
            onClick={() => setShowContent(!showContent)}
            className={`transition-all duration-100 bg-grey-fields-200 rounded-full p-1.5 ease-out transform ${
              showContent ? "" : "rotate-180"
            }`}
          >
            {/* prettier-ignore */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M17 14L12 9L7 14" stroke="#656565" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <div className={accordionClasses}>
          <p className="text-xs md:text-sm font-bold text-black-secondary">{price}</p>

          <dd className={`no-outline inline-flex items-center rounded-full flex-shrink-0`}>
            <div className="flex items-center space-x-2.5">
              <button
                className="flex-shrink-0 h-7 w-7 sm:h-8 sm:w-8 rounded-full border border-grey-border border-opacity-50 inline-flex items-center justify-center text-placeholder disabled:cursor-not-allowed"
                onClick={() => decrement(id)}
              >
                {/* prettier-ignore */}
                <svg className="w-[60%]" viewBox="0 0 18 18" fill="none">
                  <path d="M4.5 9H13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>

              <div className="flex items-stretch">
                <input
                  type="number"
                  className=" w-8 block h-7.5 sm:w-8 sm:h-8 rounded-5 border-grey-divider focus:border-primary-500 border no-outline text-center  font-semibold text-xxxs sm:text-xs text-dark"
                  value={quantity}
                  onChange={(e) => true}
                  readOnly
                />
              </div>
              <button
                onClick={() => increment(id)}
                className="flex-shrink-0 h-7 w-7 sm:h-8 sm:w-8 rounded-full border  border-grey-border border-opacity-50 inline-flex items-center justify-center text-placeholder"
              >
                {/* prettier-ignore */}
                <svg className="w-[60%]" viewBox="0 0 18 18" fill="none">
                  <path d="M4.5 9H13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9 13.5V4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
              </button>
            </div>
          </dd>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2.5 mt-2.5">
          <span className="text-accent-red-500 text-1xs font-semibold">{error}</span>
        </div>
      )}
    </>
  );
};
export default SelectVariantExtra;
